import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, Mail, Plus, Share2, Trash2, Radio } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PetAvatar } from "@/components/PetAvatar";
import { api, errMsg, fmtAgo, fmtTime } from "@/lib/api";

const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Link copied"); };
const shareLink = (url, title) => (navigator.share ? navigator.share({ title, url }).catch(() => {}) : copy(url));
const toLocalInput = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);

function Household() {
  const [h, setH] = useState(null);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const load = useCallback(async () => {
    const [a, b] = await Promise.all([api.get("/household"), api.get("/household/invites")]);
    setH(a.data); setInvites(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const invite = async (e) => {
    e?.preventDefault();
    try {
      const { data } = await api.post("/household/invites", { email: email || null });
      if (data.emailed) toast.success(`Invite emailed to ${email}`);
      else shareLink(data.link, "Join our PackLog household");
      setEmail("");
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (!h) return null;
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {h.members.map((m) => (
          <div key={m.id} className="pl-card p-3 flex items-center gap-3" data-testid={`member-${m.id}`}>
            <PetAvatar url={m.picture} name={m.name} size={44} />
            <div className="flex-1"><p className="font-semibold">{m.name}{m.is_owner && <span className="pl-chip bg-[#FFF7E6] text-[#B45309] ml-2">Owner</span>}</p><p className="text-xs text-slate-500">{m.email}</p></div>
            {h.is_owner && !m.is_owner && <button data-testid={`member-remove-${m.id}`} onClick={async () => { await api.delete(`/household/members/${m.id}`); load(); }} className="text-xs font-semibold text-slate-400">Remove</button>}
          </div>
        ))}
      </div>
      <form onSubmit={invite} className="pl-card p-4 space-y-3">
        <p className="font-heading font-semibold text-lg">Invite family & roommates</p>
        <p className="text-sm text-slate-500">They get full access to every pet. Free: unlimited caregivers.</p>
        <input type="email" className="pl-input" placeholder="Their email (optional)" data-testid="invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <button className="pl-btn-primary" data-testid="invite-send-btn" disabled={!email}><Mail className="h-4 w-4" />Email invite</button>
          <button type="button" onClick={() => invite()} className="pl-btn-ghost" data-testid="invite-link-btn"><Share2 className="h-4 w-4" />Share link</button>
        </div>
      </form>
      {invites.length > 0 && <p className="text-sm font-semibold text-slate-500">Pending invites</p>}
      {invites.map((i) => (
        <div key={i.id} className="pl-card p-3 flex items-center gap-2 text-sm" data-testid={`invite-${i.id}`}>
          <span className="flex-1">{i.email || "Link invite"} · expires {new Date(i.expires_at).toLocaleDateString()}</span>
          <button onClick={() => copy(`${window.location.origin}/join/${i.token}`)} data-testid={`invite-copy-${i.id}`}><Copy className="h-4 w-4" /></button>
          <button onClick={async () => { await api.delete(`/household/invites/${i.id}`); load(); }} data-testid={`invite-delete-${i.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

function NewLinkDialog({ open, onOpenChange, onCreated }) {
  const now = new Date();
  const [f, setF] = useState({ label: "Weekend sitter", sitter_name: "", starts_at: toLocalInput(now), expires_at: toLocalInput(new Date(now.getTime() + 3 * 864e5)), notes: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/sitter-links", { ...f, starts_at: new Date(f.starts_at).toISOString(), expires_at: new Date(f.expires_at).toISOString() });
      onCreated(data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl bg-[#FAF7F2]">
        <DialogHeader><DialogTitle className="font-heading text-2xl">New sitter link</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="sitter-link-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><label className="pl-label">Label</label><input required className="pl-input" data-testid="sitter-label-input" value={f.label} onChange={set("label")} /></div>
            <div><label className="pl-label">Sitter name</label><input className="pl-input" placeholder="Priya" data-testid="sitter-name-input" value={f.sitter_name} onChange={set("sitter_name")} /></div>
            <div><label className="pl-label">Starts</label><input type="datetime-local" className="pl-input" data-testid="sitter-start-input" value={f.starts_at} onChange={set("starts_at")} /></div>
            <div><label className="pl-label">Expires</label><input required type="datetime-local" className="pl-input" data-testid="sitter-expires-input" value={f.expires_at} onChange={set("expires_at")} /></div>
          </div>
          <div><label className="pl-label">Note for the sitter</label><textarea rows={2} className="pl-input py-3" placeholder="Spare key is under the blue pot." data-testid="sitter-notes-input" value={f.notes} onChange={set("notes")} /></div>
          <button className="pl-btn-primary w-full" data-testid="sitter-link-create-btn">Create link</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SitterLinks() {
  const [links, setLinks] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/sitter-links").then((r) => setLinks(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const tone = { active: "bg-[#ECFDF5] text-[#047857]", scheduled: "bg-[#FFF7E6] text-[#B45309]", expired: "bg-slate-100 text-slate-500", revoked: "bg-slate-100 text-slate-500" };
  return (
    <div className="space-y-3">
      <button className="pl-btn-primary w-full" data-testid="sitter-share-link-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" />Create sitter link</button>
      <p className="text-sm text-slate-500">Sitters see today's checklist and care guide, and can log tasks. No account needed. Links expire automatically.</p>
      {links.map((l) => (
        <div key={l.id} className="pl-card p-4" data-testid={`sitter-link-${l.id}`}>
          <div className="flex items-center gap-2"><Link2 className="h-4 w-4" /><p className="font-semibold flex-1">{l.label}{l.sitter_name ? ` · ${l.sitter_name}` : ""}</p>
            <span className={`pl-chip ${tone[l.state]}`} data-testid={`sitter-link-state-${l.id}`}>{l.state}</span></div>
          <p className="text-xs text-slate-500 mt-1">Until {new Date(l.expires_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
          {(l.state === "active" || l.state === "scheduled") && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              <button className="pl-btn-ghost !min-h-[40px] text-sm" onClick={() => copy(l.url)} data-testid={`sitter-link-copy-${l.id}`}><Copy className="h-4 w-4" />Copy</button>
              <button className="pl-btn-ghost !min-h-[40px] text-sm" onClick={() => shareLink(l.url, "Pet care for this trip")} data-testid={`sitter-link-share-${l.id}`}><Share2 className="h-4 w-4" />Share</button>
              <button className="pl-btn-ghost !min-h-[40px] text-sm text-[#C2410C]" onClick={async () => { await api.delete(`/sitter-links/${l.id}`); load(); }} data-testid={`sitter-link-revoke-${l.id}`}>Revoke</button>
            </div>
          )}
        </div>
      ))}
      <NewLinkDialog open={open} onOpenChange={setOpen} onCreated={(l) => { load(); shareLink(l.url, "Pet care for this trip"); }} />
    </div>
  );
}

function Feed() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/activity?limit=60").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  return (
    <div className="space-y-2" data-testid="activity-feed">
      <p className="text-xs font-semibold text-[#047857] flex items-center gap-1.5"><Radio className="h-3.5 w-3.5 animate-pulse" />Live: updates every 15 seconds</p>
      {items.length === 0 && <p className="text-sm text-slate-500">No care logged yet.</p>}
      {items.map((a) => (
        <div key={a.id} className={`pl-card p-3 text-sm ${a.via === "sitter" ? "border-[#FDE68A] bg-[#FFFBF0]" : ""}`} data-testid={`activity-${a.id}`}>
          <p><b>{a.by_name}</b>{a.via === "sitter" ? " (sitter)" : ""} logged <b>{a.title}</b> for {a.pet_name}</p>
          <p className="text-xs text-slate-500">{fmtTime(a.time)} slot · {fmtAgo(a.done_at)}</p>
        </div>
      ))}
    </div>
  );
}

export default function Share() {
  return (
    <div data-testid="share-page">
      <h1 className="font-heading text-4xl font-extrabold mb-5">Share care</h1>
      <Tabs defaultValue="sitter">
        <TabsList className="w-full grid grid-cols-3 h-12 rounded-2xl bg-[#EFECE6]">
          <TabsTrigger value="sitter" className="rounded-xl" data-testid="share-tab-sitter">Sitters</TabsTrigger>
          <TabsTrigger value="household" className="rounded-xl" data-testid="share-tab-household">Household</TabsTrigger>
          <TabsTrigger value="feed" className="rounded-xl" data-testid="share-tab-feed">Live feed</TabsTrigger>
        </TabsList>
        <TabsContent value="sitter" className="mt-4"><SitterLinks /></TabsContent>
        <TabsContent value="household" className="mt-4"><Household /></TabsContent>
        <TabsContent value="feed" className="mt-4"><Feed /></TabsContent>
      </Tabs>
    </div>
  );
}
